import { Alert } from '@creditornot/cb-alert';
import { DataTable } from '@creditornot/cb-data-table';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { DecodedValueMap, SetQuery } from 'use-query-params';

import { fetchPurchaseItemsV2 } from 'apis/merchant-gateway/merchant-analytics/api';
import { PurchaseItem } from 'apis/merchant-gateway/merchant-analytics/types';
import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import useCursorPagination from 'common/hooks/useCursorPagination';
import { localize } from 'common/utils';
import InfiniteScrollHelper from 'modules/purchases/InfiniteScrollHelper';
import PurchaseItemDetailLoader from 'modules/purchases/PurchaseItemDetailLoader';
import { useVenueContext } from 'modules/venue/VenueContext';

import { IsoDateRange } from './FilterDateRangePicker';
import { PurchasesQueryConfig } from './purchasesQueryConfig';

const DEFAULT_PAGE_SIZE = 100;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

const PurchaseItemsContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  margin-block-start: 12px;
  overflow: auto;
`;

const StyledDataTable: typeof DataTable = styled(DataTable)`
  display: flex;
  flex-direction: column;
  min-width: 780px;
`;

type PurchaseItemsTableV2Props = {
  dateRange: IsoDateRange;
  query: DecodedValueMap<PurchasesQueryConfig>;
  setQuery: SetQuery<PurchasesQueryConfig>;
};

const PurchaseItemsTable = ({ dateRange, query, setQuery }: PurchaseItemsTableV2Props) => {
  const { formatMessage, locale } = useIntl();
  const { currency, id: venueId, timezone } = useVenueContext();

  const {
    data = [],
    isError,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useCursorPagination(
    `/merchant-analytics-api/v2/venues/${venueId}/items/totals`,
    {
      venueId,
      locale,
      startDate: DateTime.fromISO(dateRange.from, { zone: timezone }).toISODate(),
      endDate: DateTime.fromISO(dateRange.to, { zone: timezone }).plus({ days: 1 }).toISODate(),
      limit: DEFAULT_PAGE_SIZE,
      sortField: 'COUNT',
      sortOrder: 'DESC',
      ...(query && query.filters && { filters: query.filters.split(',').filter(isAnalyticsFilter) }),
    },
    fetchPurchaseItemsV2,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
  const items = data.flat();

  const handleOpenPurchaseItem = useCallback(
    (itemId: string) => {
      setQuery({ itemId });
    },
    [setQuery],
  );

  const handleClosePurchaseItem = useCallback(() => {
    setQuery({ itemId: undefined });
  }, [setQuery]);

  const selectedItem = (query.itemId && items.find(item => item.itemId === query.itemId)) || null;

  const disableFetchMore = isLoading || isFetchingPreviousPage || isFetchingNextPage;

  return (
    <PurchaseItemsContainer>
      {/* Use a custom helper for infinite scroll to be able to use design system table for styles */}
      {!disableFetchMore && !!fetchPreviousPage && !!hasPreviousPage && (
        <InfiniteScrollHelper onFetchMore={fetchPreviousPage} />
      )}
      <StyledDataTable<PurchaseItem>
        horizontalPadding="wide"
        loading={isLoading}
        dataSource={items}
        getRowKey={item => item.itemId}
        onRowClick={handleOpenPurchaseItem}
        emptyMessage={{
          title: formatMessage({ id: 'purchases.messages.no-items' }),
        }}
        columns={[
          {
            id: 'item',
            name: formatMessage({ id: 'purchases.columns.item' }),
            render: ({ record }) => localize(record.product.names, locale),
          },
          {
            id: 'count',
            name: formatMessage({ id: 'purchases.columns.quantity' }),
            render: ({ record }) => record.count,
          },
          {
            id: 'itemTotal',
            name: formatMessage({ id: 'purchases.columns.item-total' }),
            render: ({ record }: { record: PurchaseItem }) => (
              <MonetaryAmount
                amount={record.pricing.itemOnlyPrice.gross + record.pricing.itemDeposit.gross}
                currency={currency}
              />
            ),
          },
          {
            id: 'totalWithOptions',
            name: formatMessage({ id: 'purchases.columns.total-with-options' }),
            render: ({ record }: { record: PurchaseItem }) => (
              <MonetaryAmount amount={record.pricing.beforeDiscountPrice.gross} currency={currency} />
            ),
          },
          {
            id: 'totalAfterDeductions',
            name: formatMessage({ id: 'purchases.columns.total-after-deduction' }),
            render: ({ record }: { record: PurchaseItem }) => (
              <MonetaryAmount amount={record.pricing.finalPrice.gross} currency={currency} />
            ),
          },
        ]}
      />
      {!disableFetchMore && !!fetchNextPage && !!hasNextPage && <InfiniteScrollHelper onFetchMore={fetchNextPage} />}
      {!isLoading && isError && (
        <ErrorAlert>
          <FormattedMessage id="errors.unknown-error-retry-contact" />
        </ErrorAlert>
      )}
      {selectedItem !== null && (
        <PurchaseItemDetailLoader
          onClose={handleClosePurchaseItem}
          selectedItem={selectedItem}
          query={query}
          dateRange={dateRange}
        />
      )}
    </PurchaseItemsContainer>
  );
};
export default PurchaseItemsTable;
